import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
    AbstractModalComponent,
    AbstractModalData,
    ModalType,
} from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

export type ModalData = {
    cancelIcon?: IconProp;
    cancelLabel?: string;
    closeAction?: boolean;
    confirmIcon?: IconProp;
    confirmLabel: string;
    onConfirm?: () => Promise<void>;
    showCloseIcon?: boolean;
} & AbstractModalData;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, RessourcesModule],
    selector: 'app-confirmation-modal',
    styleUrl: './confirmation-modal.component.scss',
    templateUrl: './confirmation-modal.component.html',
})
export class ConfirmationModal extends AbstractModalComponent {
    public override data: ModalData = inject(MAT_DIALOG_DATA);
    public dialogRef = inject(MatDialogRef<ConfirmationModal>);
    public readonly disableClose = input(true);
    public readonly isLoading = signal(false);
    public onClickEvent = output<ModalResultEnum>();

    protected readonly ButtonType = ButtonType;
    protected readonly ModalResultEnum = ModalResultEnum;
    protected readonly ModalType = ModalType;

    private readonly destroyRef = inject(DestroyRef);

    public constructor() {
        super();

        this.dialogRef.disableClose = this.disableClose();

        this.destroyRef.onDestroy(() => this.isLoading.set(false));
    }

    public onClick(result: ModalResultEnum): void {
        this.dialogRef.close(result);
    }

    public async onConfirmClick(): Promise<void> {
        if (!this.data.onConfirm) {
            this.dialogRef.close(ModalResultEnum.CONFIRM);

            return;
        }

        this.isLoading.set(true);
        try {
            await this.data.onConfirm();
            this.dialogRef.close(ModalResultEnum.CONFIRM);
        } finally {
            this.dialogRef.close();
        }
    }
}
